import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  ScreenType,
  useScreenType,
  WMS_MOBILE_MAX_WIDTH,
} from "@redotech/react-util/screen";
import { StorageLocationTableRow } from "@redotech/redo-fulfillment-wms-common/schema/storage-location";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonGroup } from "@redotech/redo-web/arbiter-components/buttons/redo-button-group";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import { LotBadge } from "../shared/lot-badge";
import { StorageLocationItemInfo } from "./storage-location-item-cell";

type InventoryItem = StorageLocationTableRow["inventoryItems"][number];

type AdjustmentMode = "adjust" | "set";

export interface AdjustInventoryTarget {
  storageLocationId: StorageLocationId;
  binName: string;
  item: InventoryItem;
}

export const AdjustInventoryModal = memo(function AdjustInventoryModal({
  target,
  onClose,
  onSuccess,
  rpcClient,
}: {
  target: AdjustInventoryTarget | null;
  onClose: () => void;
  onSuccess: (result: {
    previousQuantity: number;
    newQuantity: number;
    /** The product was unassigned from the bin, so its row is gone. */
    unassigned: boolean;
  }) => void;
  rpcClient: RedoOutboundLabelsRpcClient;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AdjustmentMode>("adjust");
  // Each mode keeps its own value: "adjust by" accepts negatives and "set to"
  // does not, so sharing one number would clamp the user's entry on switch.
  const [adjustBy, setAdjustBy] = useState<number | undefined>(undefined);
  const [setTo, setSetTo] = useState<number | undefined>(undefined);
  const [note, setNote] = useState("");
  const [unassignWhenEmpty, setUnassignWhenEmpty] = useState(true);
  const isMobile = useScreenType(WMS_MOBILE_MAX_WIDTH) === ScreenType.MOBILE;
  const quantityInputSize = isMobile ? "regular" : "small";

  // Reopening for a different item shouldn't inherit the last item's entries.
  useEffect(() => {
    if (target) {
      setMode("adjust");
      setAdjustBy(undefined);
      setSetTo(undefined);
      setNote("");
      setUnassignWhenEmpty(true);
    }
  }, [target]);

  const handleClose = useHandler(() => {
    onClose();
  });

  const [saveLoad, triggerSave] = useTriggerLoad(async () => {
    if (!target || amount === undefined) {
      return;
    }
    try {
      const result = await rpcClient.adjustInventoryLevel({
        storageLocationId: target.storageLocationId,
        productId: target.item.productInfo?.id,
        inventoryItemId: target.item.inventoryItemId,
        mode,
        amount,
        note: note.trim() || undefined,
        unassignIfEmpty: willUnassign,
      });
      const { previousQuantity, newQuantity } = result;
      const unassigned = !!result.unassigned;
      toast(
        unassigned
          ? previousQuantity === newQuantity
            ? t("Unassigned from {{bin}}.", { bin: target.binName })
            : t(
                "Adjusted {{bin}} from {{previous}} to {{next}} and unassigned it.",
                {
                  bin: target.binName,
                  previous: previousQuantity,
                  next: newQuantity,
                },
              )
          : t("Adjusted {{bin}} from {{previous}} to {{next}}.", {
              bin: target.binName,
              previous: previousQuantity,
              next: newQuantity,
            }),
        { variant: "success" },
      );
      if (willUnassign && !unassigned) {
        toast(
          t(
            "{{bin}} is still assigned because it has an open transfer or replenishment.",
            { bin: target.binName },
          ),
          { variant: "warning" },
        );
      }
      onSuccess({ previousQuantity, newQuantity, unassigned });
      onClose();
    } catch (error) {
      toast(
        error instanceof Error && error.message
          ? t("Failed to adjust inventory: {{message}}", {
              message: error.message,
            })
          : t("Failed to adjust inventory. Please try again."),
        { variant: "error" },
      );
    }
  });

  const currentQuantity = target?.item.quantity ?? 0;
  const amount = mode === "adjust" ? adjustBy : setTo;
  // With no amount entered yet the preview shows no change rather than a zero.
  const resultingQuantity =
    amount === undefined
      ? currentQuantity
      : mode === "set"
        ? amount
        : Math.max(0, currentQuantity + amount);
  const endsEmpty = amount !== undefined && resultingQuantity === 0;
  const willUnassign = endsEmpty && unassignWhenEmpty;

  const unassignOption = endsEmpty && (
    <RedoCheckbox
      description={t(
        "Remove this product from the bin so it no longer shows here.",
      )}
      label={t("Unassign from bin")}
      setValue={(value) => setUnassignWhenEmpty(value === true)}
      size={isMobile ? "md" : "sm"}
      value={unassignWhenEmpty}
    />
  );

  return (
    <RedoModal
      buttonPlacement={isMobile ? "full" : undefined}
      footerLayout={isMobile ? "column" : undefined}
      isOpen={!!target}
      onModalCloseRequested={handleClose}
      primaryButton={{
        text: t("Save"),
        onClick: () => triggerSave(),
        // At zero already, saving with unassign checked still removes the row.
        disabled:
          amount === undefined ||
          (resultingQuantity === currentQuantity && !willUnassign),
        pending: saveLoad.pending,
        size: isMobile ? "lg" : "sm",
      }}
      secondaryButton={{
        text: t("Cancel"),
        onClick: handleClose,
        size: isMobile ? "lg" : "sm",
      }}
      subtitle={target ? t("Bin {{bin}}", { bin: target.binName }) : undefined}
      title={t("Adjust inventory")}
    >
      {isMobile ? (
        <Flex dir="column" gap="lg">
          {target && (
            <Flex dir="column" gap="sm">
              <StorageLocationItemInfo item={target.item} showQuantity />
              {target.item.lotNumber && (
                <Flex align="center" gap="xs">
                  <Text fontSize="xs" textColor="secondary">
                    {t("Lot")}
                  </Text>
                  <LotBadge
                    expirationDate={target.item.expirationDate}
                    lotNumber={target.item.lotNumber}
                  />
                </Flex>
              )}
            </Flex>
          )}
          <Flex dir="column" gap="xs">
            <RedoButtonGroup>
              <RedoButton
                flexGrow={1}
                hierarchy={mode === "adjust" ? "primary" : "secondary"}
                onClick={() => setMode("adjust")}
                size="md"
                text={t("Adjust by")}
              />
              <RedoButton
                flexGrow={1}
                hierarchy={mode === "set" ? "primary" : "secondary"}
                onClick={() => setMode("set")}
                size="md"
                text={t("Set to")}
              />
            </RedoButtonGroup>
            <Text fontSize="xs" textColor="secondary">
              {mode === "adjust"
                ? t("Use a negative number to decrease the quantity.")
                : t("Set the quantity in this bin to a specific amount.")}
            </Text>
          </Flex>
          <RedoIncrementDecrement
            fullWidth
            key={mode}
            min={mode === "set" ? 0 : undefined}
            setValue={mode === "adjust" ? setAdjustBy : setSetTo}
            size="regular"
            value={mode === "adjust" ? adjustBy : setTo}
          />
          <Flex align="center" gap="sm" justify="center">
            <Text fontSize="lg" textColor="secondary">
              {`${currentQuantity} →`}
            </Text>
            <Text
              fontSize="2xl"
              fontWeight="semibold"
              textColor={
                resultingQuantity > currentQuantity
                  ? "success"
                  : resultingQuantity < currentQuantity
                    ? "error"
                    : "secondary"
              }
            >
              {resultingQuantity}
            </Text>
          </Flex>
          {unassignOption}
          <RedoTextInput
            placeholder={t("Add a note (optional)")}
            setValue={setNote}
            size="md"
            value={note}
          />
        </Flex>
      ) : (
        <Flex dir="column" gap="xl">
          {target && (
            <Flex dir="column" gap="sm">
              <StorageLocationItemInfo item={target.item} showQuantity />
              {target.item.lotNumber && (
                <Flex align="center" gap="xs">
                  <Text fontSize="xs" textColor="secondary">
                    {t("Lot")}
                  </Text>
                  <LotBadge
                    expirationDate={target.item.expirationDate}
                    lotNumber={target.item.lotNumber}
                  />
                </Flex>
              )}
            </Flex>
          )}

          <Flex align="flex-start" dir="column" gap="md">
            <RedoRadioButton
              label={
                <Flex dir="column" gap="xs">
                  <Text fontSize="sm" fontWeight="medium">
                    {t("Adjust by...")}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    {t(
                      "Change the current quantity by a given amount. Use a negative number to decrease it.",
                    )}
                  </Text>
                </Flex>
              }
              onSelect={() => setMode("adjust")}
              selected={mode === "adjust"}
              size="sm"
            />
            {mode === "adjust" && (
              <Flex pl="3xl">
                <RedoIncrementDecrement
                  setValue={setAdjustBy}
                  size={quantityInputSize}
                  value={adjustBy}
                  widthHint="1000"
                />
              </Flex>
            )}
          </Flex>

          <Flex align="flex-start" dir="column" gap="md">
            <RedoRadioButton
              label={
                <Flex dir="column" gap="xs">
                  <Text fontSize="sm" fontWeight="medium">
                    {t("Set to...")}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    {t("Set the quantity in this bin to a specific amount.")}
                  </Text>
                </Flex>
              }
              onSelect={() => setMode("set")}
              selected={mode === "set"}
              size="sm"
            />
            {mode === "set" && (
              <Flex pl="3xl">
                <RedoIncrementDecrement
                  min={0}
                  setValue={setSetTo}
                  size={quantityInputSize}
                  value={setTo}
                  widthHint="1000"
                />
              </Flex>
            )}
          </Flex>

          <Flex align="center" gap="xs">
            <Text fontSize="xs" textColor="secondary">
              {`${currentQuantity} →`}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="medium"
              textColor={
                resultingQuantity > currentQuantity
                  ? "success"
                  : resultingQuantity < currentQuantity
                    ? "error"
                    : "secondary"
              }
            >
              {resultingQuantity}
            </Text>
          </Flex>

          {unassignOption}

          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="medium">
              {t("Note (optional)")}
            </Text>
            <RedoTextInput
              placeholder={t("Add a note...")}
              setValue={setNote}
              size="sm"
              value={note}
            />
          </Flex>
        </Flex>
      )}
    </RedoModal>
  );
});
